import { useMemo } from 'react';
import {
  BulkFieldEditDialog,
  type BulkFieldSpec,
  type BulkResult,
} from '@/components/common/BulkFieldEditDialog';
import { equipmentApi, type BulkEquipmentChanges } from '@/api/services/equipment.api';
import type { Location } from '@/api/services/locations.api';

/**
 * Which equipment fields a bulk edit may set.
 *
 * `assetNumber`, `name` and `serialNumber` identify one asset, so they are not
 * here. Neither is the parent: re-parenting needs a cycle check against each
 * asset's own ancestry, and a selection can easily hold both a parent and its
 * child, so it belongs in its own operation.
 */
const BASE_FIELDS: BulkFieldSpec[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'AVAILABLE', label: 'Available' },
      { value: 'IN_USE', label: 'In use' },
      { value: 'MAINTENANCE', label: 'Maintenance' },
      { value: 'REPAIR', label: 'Repair' },
      { value: 'RETIRED', label: 'Retired' },
      { value: 'DISPOSED', label: 'Disposed' },
    ],
  },
  {
    key: 'condition',
    label: 'Condition',
    type: 'select',
    options: [
      { value: 'EXCELLENT', label: 'Excellent' },
      { value: 'GOOD', label: 'Good' },
      { value: 'FAIR', label: 'Fair' },
      { value: 'POOR', label: 'Poor' },
      { value: 'CRITICAL', label: 'Critical' },
    ],
  },
  {
    key: 'category',
    label: 'Category',
    type: 'select',
    options: [
      { value: 'DRILLING', label: 'Drilling' },
      { value: 'COMPLETION', label: 'Completion' },
      { value: 'PRODUCTION', label: 'Production' },
      { value: 'TESTING', label: 'Testing' },
      { value: 'SAFETY', label: 'Safety' },
      { value: 'TRANSPORTATION', label: 'Transportation' },
      { value: 'OTHER', label: 'Other' },
    ],
  },
  {
    key: 'nextServiceDate',
    label: 'Next service date',
    type: 'date',
  },
];

interface BulkEquipmentEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  equipmentIds: string[];
  locations: Location[];
  onApplied: (result: BulkResult) => void;
}

export function BulkEquipmentEditDialog({
  open,
  onOpenChange,
  equipmentIds,
  locations,
  onApplied,
}: BulkEquipmentEditDialogProps) {
  // Location is a select over the company's own locations, so a destination the
  // server would reject cannot be chosen in the first place.
  const fields = useMemo<BulkFieldSpec[]>(
    () => [
      ...BASE_FIELDS,
      {
        key: 'locationId',
        label: 'Location',
        type: 'select',
        options: locations.map((l) => ({ value: l.id, label: l.name })),
        hint: 'Moves every selected asset to this location',
      },
    ],
    [locations]
  );

  return (
    <BulkFieldEditDialog
      open={open}
      onOpenChange={onOpenChange}
      ids={equipmentIds}
      fields={fields}
      noun="asset"
      submit={(ids, changes) =>
        equipmentApi.bulkUpdate({ equipmentIds: ids, changes: changes as BulkEquipmentChanges })
      }
      describeRow={(row) => ({
        title: (row.assetNumber as string) ?? row.id,
        subtitle: row.name as string | undefined,
      })}
      onApplied={onApplied}
    />
  );
}
